import { ArrowLeft, ListMusic } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
  Button,
  EmptyState,
  Skeleton,
  StatusMessage,
} from "@ceedero/ui";

import { useCloudLibrary, useRemoveLibraryProject } from "@/cloud/hooks/use-cloud-pull";
import { AppPage } from "@/components/app-page";
import { useTranslation } from "@/localization/hooks/use-translation";
import { ROUTES } from "@/navigation/constants/routes";
import { ProjectFacts } from "@/music-catalog/components/project-detail-page/project-facts";
import { ProjectHero } from "@/music-catalog/components/project-detail-page/project-hero";
import { ProjectTracklist } from "@/music-catalog/components/project-detail-page/project-tracklist";
import {
  buildDuplicateGroups,
  collectCatalogTracks,
  collectDuplicateEntryIds,
  countDuplicatesByProject,
  selectLiveProjects,
} from "@/music-catalog/utils/catalog-view";

/**
 * One project: its artwork, what is known about it, and its tracklist.
 *
 * Read from the same pull the catalog page uses rather than from a request of
 * its own — the whole library is already in hand, so opening a project costs
 * nothing. It also means arriving here by a link, with no pull yet made, is a
 * normal load rather than a special case.
 *
 * What is deliberately absent is the artwork's dimensions and its layers. Those
 * live inside the project's bytes, and this application owns no project codec:
 * reading them would mean parsing a document here, which is the editor's job
 * and the one thing this application is not.
 */
export const ProjectDetailPage = () => {
  const { t } = useTranslation();
  const { desktopProjectId } = useParams();
  const library = useCloudLibrary();
  const removeProject = useRemoveLibraryProject();
  const navigate = useNavigate();
  const [isConfirmingRemoval, setIsConfirmingRemoval] = useState(false);

  const project = useMemo(
    () =>
      library.data
        ? selectLiveProjects(library.data.projects).find(
            (candidate) => candidate.desktopProjectId === desktopProjectId,
          )
        : undefined,
    [desktopProjectId, library.data],
  );

  // Duplicates are a fact about the whole catalog rather than about this
  // project, so they are resolved over everything and then read for these rows.
  const duplicates = useMemo(() => {
    if (!library.data) {
      return { byProject: new Map<string, number>(), entryIds: new Set<string>() };
    }
    const groups = buildDuplicateGroups(collectCatalogTracks(library.data));
    return {
      byProject: countDuplicatesByProject(groups),
      entryIds: collectDuplicateEntryIds(groups),
    };
  }, [library.data]);

  return (
    <AppPage width="full">
      {/*
        A control with a surface of its own rather than tinted text. Every other
        page puts its back link on the page background, where muted text is
        legible by design; this one puts it over the album's own colour, and a
        grey word on a photograph is legible only by luck.

        `outline` rather than `secondary`, which is the quietest surface the
        theme has and disappeared into a dark album exactly as the bare text
        did: this one is the page's own background behind a border and a raised
        shadow, so it reads as a control against any artwork the reader has and
        in either theme.
      */}
      <Button
        asChild
        type="button"
        variant="outline"
        size="sm"
        // Lifted above the hero below it. That hero reaches upward behind this
        // link with its colour, and being later in the document its whole
        // stacking context paints over anything earlier that is not positioned —
        // so the button was drawn, and then the glow was drawn on top of it.
        className="relative z-10 w-fit"
      >
        <Link to={ROUTES.catalog}>
          <ArrowLeft aria-hidden />
          {t("project.back")}
        </Link>
      </Button>

      {library.isPending && <Skeleton className="h-64 w-full" />}
      {library.isError && <StatusMessage tone="error">{t("catalog.unavailable")}</StatusMessage>}

      {library.data && project === undefined && (
        <EmptyState
          icon={<ListMusic />}
          title={t("project.notFound.title")}
          description={t("project.notFound.description")}
        />
      )}

      {project !== undefined && (
        <div className="flex flex-col gap-10">
          <ProjectHero
            duplicateCount={duplicates.byProject.get(project.desktopProjectId) ?? 0}
            onRemove={() => setIsConfirmingRemoval(true)}
            project={project}
          />

          {removeProject.isError && (
            <StatusMessage tone="error">{t("catalog.removeFailed")}</StatusMessage>
          )}

          {/*
            The tracklist is what the user came for, so it takes the wider column
            and the pressing details sit beside it rather than above. Each side
            is headed rather than boxed: two bordered panels side by side read as
            two applications sharing a page, and the rule under each heading is
            all the separation a column needs.
          */}
          <div className="relative z-10 grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,20rem)] lg:gap-16">
            <section className="flex flex-col gap-4" aria-label={t("project.tracklist")}>
              <h2 className="border-b border-border/60 pb-2 font-display text-xs font-semibold tracking-[0.12em] text-muted-foreground uppercase">
                {t("project.tracklist")}
              </h2>
              {/*
                Pulled out to the gutter the rows pad themselves with, so the
                track titles line up with the heading above them rather than
                sitting indented under it.
              */}
              <div className="-mx-3">
                <ProjectTracklist duplicateEntryIds={duplicates.entryIds} project={project} />
              </div>
            </section>

            <section className="flex flex-col gap-4" aria-label={t("project.details")}>
              <h2 className="border-b border-border/60 pb-2 font-display text-xs font-semibold tracking-[0.12em] text-muted-foreground uppercase">
                {t("project.details")}
              </h2>
              <ProjectFacts project={project} />
            </section>
          </div>
        </div>
      )}

      {/*
        Asked about because it is the cloud's only copy. The confirmation is the
        one place it can be said that the device that saved it keeps its own.
      */}
      <AlertDialog open={isConfirmingRemoval} onOpenChange={setIsConfirmingRemoval}>
        <AlertDialogContent>
          <AlertDialogTitle>{t("catalog.removeTitle")}</AlertDialogTitle>
          <AlertDialogDescription>{t("catalog.removeDescription")}</AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button type="button" variant="ghost">
                {t("account.cancel")}
              </Button>
            </AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              isLoading={removeProject.isPending}
              onClick={() => {
                if (project) {
                  setIsConfirmingRemoval(false);
                  // Back to the catalog on success: this page is about a
                  // project that no longer exists, and staying would answer the
                  // removal with "that project is not in your catalog".
                  removeProject.mutate(project.desktopProjectId, {
                    onSuccess: () => navigate(ROUTES.catalog),
                  });
                }
              }}
            >
              {t("catalog.remove")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppPage>
  );
};
